<template lang="pug">
TableTitle(title="Patient Referrals",
  searchPlaceholder="Search by patient name or hospital"
  v-model="searchKeyword",
  @doSearch="fetchPatientNavigation",
  @blur="fetchPatientNavigation"
)
  template(#actions)
    select.table-action-selector.table-action-selector--insurance.provider_type(
      :title="patientNavigationTitle",
      @change="setPatientNavigationFilter($event.target.value)"
      style="margin-right: 24px;"
    )
      option(value="") PROVIDER TYPE
      option(
        v-for="setting in patientNavigationSettings"
        :key="setting.id"
        :value="setting.id"
      ) {{ setting.name }}

.table.table--hoverable
  .th
    .td(@click.prevent="changeListOrder('DateTime', fetchPatientNavigation)" style="cursor: pointer;") Date/Time
      i(style="font-weight: bolder;") {{ DateTimeColumnSortingIcon }}
    .td(@click.prevent="changeListOrder('PatientName', fetchPatientNavigation)" style="cursor: pointer;") Patient Name
      i(style="font-weight: bolder;") {{ PatientNameColumnSortingIcon }}
    .td(@click.prevent="changeListOrder('ProviderName', fetchPatientNavigation)" style="cursor: pointer;") Provider Name
      i(style="font-weight: bolder;") {{ ProviderNameColumnSortingIcon }}
    .td(@click.prevent="changeListOrder('ReferralType', fetchPatientNavigation)" style="cursor: pointer;") Referral Type
      i(style="font-weight: bolder;") {{ ReferralTypeColumnSortingIcon }}
    .td(@click.prevent="changeListOrder('Status', fetchPatientNavigation)" style="cursor: pointer;") Status
      i(style="font-weight: bolder;") {{ StatusColumnSortingIcon }}
  .tr(
    v-if='referrals'
    v-for="referral in referrals",
    :key="referral.id",
    @click="openReferralRecordOverlay(referral)"
  )
    .td
      div {{ getDateFromUnixFormatted(referral.scheduledAt) }}
      .second-row-md-grey {{ referral.timeOfAppointment || '' }}
    .td.bold {{ getUserFormatted(referral.patient) }}
    .td {{ getProviderName(referral) }}
    .td(:class="{ 'referral-type-disabled': isReferralDisabled(referral) }") {{ getReferralType(referral) }}
    .td
      .green(v-if="isStatusGreen(referral.status)") {{ referral.status }}
      .yellow(v-else-if="isStatusYellow(referral.status)") {{ referral.status }}
      .red(v-else-if="isStatusRed(referral.status)") {{ referral.status }}
      .black(v-else) {{ referral.status }}
      .second-row-sm-grey Updated {{ getRelativeTimeFromUnixFormatted(referral.updatedAt) }}
        
Pagination(
  v-if="pagination && referrals != null && referrals.length",
  :pagination="pagination",
  :reloadFn="fetchPatientNavigation"
  @changePagination="changePagination"
)
.no-results(v-if="referrals != null && !referrals.length") 
  base-icon(name="exclamation") 
  span No matching records

ReferralRecordOverlay(
  :key="`${componentKey}-1`",
  :showReferral="showReferral",
  :isOpen="isReferralRecordOverlayOpen",
  @closeReferralRecordOverlay="closeReferralRecordOverlay",
  @refreshPatientReferrals="refreshPatientReferrals"
)

PatientDetailsOverlay(
  :key="`${componentKey}-2`",
  :isOpen="isPatientDetailsOverlayOpen",
  :showPatient="showPatient"
  @closePatientDetailsOverlay="closePatientDetailsOverlay",
)

</template>

<script>
import { defineComponent } from "vue";
import TableTitle from "@/components/TableTitle";
import DateSelector from "@/components/DateSelector";
import Pagination from "@/components/Pagination";
import listPagesMixin from "@/mixins/listPagesMixin";

import ReferralRecordOverlay from "@/components/cco-patient-navigation/patient-navigation/overlays/ReferralRecordOverlay.vue";
import ThreeDots from "@/components/cco-patient-navigation/patient-navigation/ThreeDots";
import PatientDetailsOverlay from "@/components/cco-patient-navigation/patient-navigation/overlays/PatientDetailsOverlay.vue";
import { fetchCommunityCareOrganizationById } from "@/api/communityOrganization";
import { fetchPatientNavigation } from "@/api/patientNavigation";
import { fetchPatientNavigationSettings } from "@/api/patientNavigationSettingsApi";

import { apiStatus, apiStatusComputedFactory } from "@/api";
const { IDLE, PENDING, SUCCESS, ERROR } = apiStatus;
import {
  getDateFromUnixFormatted,
  getTimeFromUnixFormatted,
  getRelativeTimeFromUnixFormatted,
  getUserFormatted,
  getDoctorFormatted,
} from "@/helpers";
import sortCCOReferrals from "@/mixins/sortCCOReferrals";

export default defineComponent({
  components: {
    TableTitle,
    DateSelector,
    Pagination,
    ReferralRecordOverlay,
    PatientDetailsOverlay,
    ThreeDots,
  },
  mixins: [listPagesMixin, sortCCOReferrals],
  computed: {
    ...apiStatusComputedFactory("fetchPatientNavigationStatus"),
  },

  data() {
    return {
      accessToPatients: false,
      searchKeyword: "",
      isReferralRecordOverlayOpen: false,
      isPatientDetailsOverlayOpen: false,
      fetchPatientNavigationStatus: IDLE,
      referrals: null,
      date: null,
      showReferral: null,
      showPatient: null,
      referralTypeMap: {},
      patientNavigationSettings: [],
      patientNavigationTitle: "PATIENT NAVIGATION",
      selectedPatientNavigationSettingId: null,
    };
  },
  emits: ["refreshAppointmentsSummary"],
  methods: {
    refreshPatientReferrals() {
      this.fetchPatientNavigation();
      this.$emit("refreshAppointmentsSummary");
    },
    getDateFromUnixFormatted,
    getTimeFromUnixFormatted,
    getRelativeTimeFromUnixFormatted,
    getUserFormatted,
    getDoctorFormatted,
    filterByDate(date) {
      this.date = date;
      this.fetchPatientNavigation();
    },
    getProviderName(referral) {
      if (referral.providerLocation) {
        return referral.providerLocation;
      } else return "N/A";
    },
    getReferralType(referral) {
      if (referral?.patientNavigationSetting?.name) {
        return referral.patientNavigationSetting.name;
      }

      const referralTypeId =
        referral?.patientNavigationSetting?.id ??
        referral?.referralType ??
        referral?.idPatientNavigationSetting ??
        null;

      if (referralTypeId != null) {
        const mappedValue = this.referralTypeMap[String(referralTypeId)];
        if (mappedValue) {
          return mappedValue;
        }
      }

      return "N/A";
    },
    isReferralDisabled(referral) {
      const setting = this.patientNavigationSettings.find(
        (s) => s.id === referral.idPatientNavigationSetting
      );
      return setting?.disabled || false;
    },
    isStatusGreen(status) {
      return ["New", "Completed"].includes(status);
    },
    isStatusYellow(status) {
      return ["1st Follow Up Due"].includes(status);
    },
    isStatusRed(status) {
      return ["Final Follow Up Due", "Rescheduled", "Cancelled"].includes(
        status
      );
    },
    async fetchPatientNavigation() {
      this.fetchPatientNavigationStatus = PENDING;
      const order = this.listOrder
        ? {
            by: this.listOrder.by,
            direction: this.listOrder.direction,
          }
        : null;

      try {
        const res = await fetchPatientNavigation({
          params: {
            ...this.reqPagination,
            order,
            idCommunity: this.$route.params.idCommunity,
            include: ["communityCare", "patient", "cancelled"],
            stage: "ARCHIVES",
            scheduledAt: this.date ?? null,
            idPatientNavigationSetting:
              this.selectedPatientNavigationSettingId ?? null,
            ...(this.searchKeyword
              ? { searchKeyword: this.searchKeyword }
              : {}),
          },
        });

        this.referrals = res.data.patientNavigations;
        this.pagination = res.data.pagination;
        this.fetchPatientNavigationStatus = SUCCESS;
      } catch (err) {
        this.fetchPatientNavigationStatus = ERROR;
        console.error(err);
      }
    },
    openPatientDetailsOverlay(patient) {
      this.componentKey = this.componentKey + 1;
      this.showPatient = patient;
      this.isPatientDetailsOverlayOpen = true;
    },
    closePatientDetailsOverlay() {
      this.isPatientDetailsOverlayOpen = false;
    },
    openReferralRecordOverlay(referral) {
      this.componentKey = this.componentKey + 1;
      this.showReferral = referral;
      this.isReferralRecordOverlayOpen = true;
    },
    closeReferralRecordOverlay() {
      this.isReferralRecordOverlayOpen = false;
    },
    async fetchCommunityCareOrganizationById() {
      try {
        const res = await fetchCommunityCareOrganizationById(
          this.$route.params.idCommunity
        );
        this.accessToPatients = res.data.hasPatientAccess;
      } catch (err) {
        console.error(err);
      }
    },
    async loadReferralTypeSettings() {
      try {
        const [enabledResponse, disabledResponse] = await Promise.all([
          fetchPatientNavigationSettings({
            params: {
              size: 200,
              filters: { disabled: false },
              order: { by: "patient-navigation-name", direction: "asc" },
            },
          }),
          fetchPatientNavigationSettings({
            params: {
              size: 200,
              filters: { disabled: true },
              order: { by: "patient-navigation-name", direction: "asc" },
            },
          }),
        ]);

        const enabledSettings =
          enabledResponse.data?.patientNavigationSettings || [];
        const disabledSettings =
          disabledResponse.data?.patientNavigationSettings || [];
        const allSettings = [...enabledSettings, ...disabledSettings];

        const { data } = { data: { patientNavigationSettings: allSettings } };
        const settings = data?.patientNavigationSettings || [];
        const lookup = {};
        settings.forEach((setting) => {
          if (setting?.id && setting?.name) {
            lookup[String(setting.id)] = setting.name;
          }
        });
        this.referralTypeMap = lookup;
        this.patientNavigationSettings = settings;
      } catch (error) {
        console.error(error);
        this.referralTypeMap = {};
        this.patientNavigationSettings = [];
      }
    },
    setPatientNavigationFilter(value) {
      if (value) {
        this.selectedPatientNavigationSettingId = Number(value);
        const setting = this.patientNavigationSettings.find(
          (item) => String(item.id) === String(value)
        );
        this.patientNavigationTitle = setting?.name || "PATIENT NAVIGATION";
      } else {
        this.selectedPatientNavigationSettingId = null;
        this.patientNavigationTitle = "PATIENT NAVIGATION";
      }
      this.fetchPatientNavigation();
    },
  },
  created() {
    if (this.$route.params.idCommunity === null) return;
    this.loadReferralTypeSettings();
    this.fetchPatientNavigation();
    this.fetchCommunityCareOrganizationById();
  },
});
</script>

<style scoped lang="scss">
.table {
  overflow: visible;
}
select.provider_type {
  width: 182px;
}

.active .three_dots_wrap .dot_popup {
  display: inline;
}
.tr {
  cursor: pointer;
}
.td {
  &:nth-child(1) {
    width: math.percentage(math.div(180, $tabContentCardWidth));
  }
  &:nth-child(2) {
    width: math.percentage(math.div(213, $tabContentCardWidth));
  }
  &:nth-child(3) {
    width: math.percentage(math.div(220, $tabContentCardWidth));
  }
  &:nth-child(4) {
    width: math.percentage(math.div(240, $tabContentCardWidth));
  }
  &:nth-child(5) {
    width: math.percentage(math.div(260, $tabContentCardWidth));
  }
  &:nth-child(6) {
    overflow: visible;
  }
}

.referral-type-disabled {
  color: #999 !important;
  text-decoration: line-through;
  opacity: 0.7;
}
</style>
